import React, { memo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';

import {
	Button,
	CloseButton,
	Confirm,
	DynamicIconWrapper,
	Icons,
	Modal,
	SpinnerOverlay } from '@components/common';
import CreateCartRequestsForm from '@components/ModalComponents/CreateCartRequestsForm/CreateCartRequestsForm';

import { useMutateDeleteAssetsFromCart } from '@hooks/assets/useMutateDeleteAssetsFromCart';
import { useMutateSendAssetCartRequest } from '@hooks/assets/useMutateSendAssetCartRequest';
import { useQueryAssetCartRequestCount } from '@hooks/assets/useQueryAssetCartRequestCount';
import { useQueryAssetCartRequestOptions } from '@hooks/assets/useQueryAssetCartRequestOptions';
import { useQueryAssetCartRequestShortViewAvailable } from '@hooks/assets/useQueryAssetCartRequestShortViewAvailable';
import {
	useQueryAssetCartRequestShortViewUnavailable,
} from '@hooks/assets/useQueryAssetCartRequestShortViewUnavailable';

import { EMenuActionsTypes } from '@enums/common';

import { MODAL_WIDTH_455 } from '@constants/modals';
import { routes } from '@constants/routes';
import { tooltipLabel } from './constants';

import AssetCartRequestAvailable from './AssetCartRequestAvailable';
import AssetCartRequestUnavailable from './AssetCartRequestUnavailable';
import { prepareAssetCartRequestList } from './prepareAssetCartRequestList';

import styles from './AssetCartRequest.module.scss';

const collapsibleBlocksInitialState = {
	availableForRequest: false,
	unavailableForRequest: false,
};

const AssetCartRequest = () => {
	const navigate = useNavigate();

	const { adminPanel: { index, requestListPath } } = routes;

	const assetCartRequestRef = useRef<HTMLDivElement | null>(null);
	const assetCartRequestIconRef = useRef<HTMLDivElement | null>(null);

	const [isAssetCartRequestOpen, setAssetCartRequestOpen] = useState<boolean>(false);
	const [isCommentsForm, setIsCommentsForm] = useState(false);
	const [isClearCartConfirm, setIsClearCartConfirm] = useState(false);

	const toggleCommentsForm = () => setIsCommentsForm(!isCommentsForm);

	const toggleClearCartConfirm = () => setIsClearCartConfirm(prev => !prev);

	const openCommentsForm = () => setIsCommentsForm(true);
	const closeCommentsForm = () => setIsCommentsForm(false);

	const [
		collapsibleBlocks,
		setCollapsibleBlocks,
	] = useState<Record<string, boolean>>(() => ({
		...collapsibleBlocksInitialState,
		availableForRequest: true,
		assetAttributes: true,
	}));

	const { assetCartRequestListCount } = useQueryAssetCartRequestCount();

	const {
		handleSendAssetCartRequest,
		isLoadingSendAssetCartRequest,
	} = useMutateSendAssetCartRequest();

	const {
		handleDeleteAssets,
		isLoadingDeleteAssets,
	} = useMutateDeleteAssetsFromCart();

	const isLoadingAssetCartRequest = (
		isLoadingSendAssetCartRequest ||
		isLoadingDeleteAssets
	);

	const {
		ownersOptions,
		assetTypesOptions,
	} = useQueryAssetCartRequestOptions();

	const {
		assetCartRequestListAvailable,
		availableAssetRequestCount,
		numberOfFutureRequests,
		isLoadingAssetCartRequestAvailable,
		fetchNextPageAvailableList,
		hasNextPageAvailableList,
	} = useQueryAssetCartRequestShortViewAvailable();

	const {
		assetCartRequestListUnavailable,
		isLoadingAssetCartRequestUnavailable,
		unavailableAssetRequestCount,
		fetchNextPageUnavailableList,
		hasNextPageUnavailableList,
	} = useQueryAssetCartRequestShortViewUnavailable();

	const assetCartRequestClasses = cn(styles.assetCartRequestContainer, {
		[styles.showList]: isAssetCartRequestOpen,
	});

	const isAssetCartRequestShown = assetCartRequestListCount === 0;

	const assetCartRequestIconClasses = cn(styles.assetCartRequestIcon, {
		[styles.assetCartRequestIconHidden]: isAssetCartRequestShown,
	});

	const assetCartRequestIcon = isAssetCartRequestOpen ? Icons.list : Icons.listWhite;

	const onClickSendRequest = () => {
		openCommentsForm();

		setAssetCartRequestOpen(false);
	};

	const onClickClearAll = async () => {
		try {
			await handleDeleteAssets({ ids: [], actionType: EMenuActionsTypes.all });

			setAssetCartRequestOpen(false);
			toggleClearCartConfirm();
		} catch {
			return;
		}
	};

	const onClickFullView = () => navigate(`${index}${requestListPath}`);

	const toggleAssetCartRequestList = () => setAssetCartRequestOpen(prevState => !prevState);

	const toggleCollapsibleBlock = (name: string) => {
		const isAlreadyOpen = collapsibleBlocks[name];

		if (isAlreadyOpen) setCollapsibleBlocks(collapsibleBlocksInitialState);
		else {
			setCollapsibleBlocks(prevState => {
				const newState: Record<string, boolean> = {};

				for (const key in prevState) {
					newState[key] = key === name;
				}

				return newState;
			});
		}
	};

	return (
		<>
			<div
				ref={assetCartRequestIconRef}
				className={assetCartRequestIconClasses}
				onClick={toggleAssetCartRequestList}
			>
				<DynamicIconWrapper
					icon={assetCartRequestIcon}
					isActive={isAssetCartRequestOpen}
					tooltipLabel={tooltipLabel}
				/>
				<div className={styles.assetCartRequestCounter}>
					{assetCartRequestListCount}
				</div>
			</div>
			<div
				ref={assetCartRequestRef}
				className={assetCartRequestClasses}
			>
				{isLoadingAssetCartRequest && <SpinnerOverlay />}
				<div className={styles.header}>
					<Button
						icon={Icons.fullView()}
						onClick={onClickFullView}
						secondary
						text='Full view'
					/>
					<div className={styles.closeButton}>
						<CloseButton onClick={toggleAssetCartRequestList} />
					</div>
				</div>
				<div>
					<AssetCartRequestAvailable
						assetCartRequestAvailableList={prepareAssetCartRequestList(assetCartRequestListAvailable)}
						assetTypesOptions={assetTypesOptions}
						availableAssetRequestCount={availableAssetRequestCount}
						getMoreAssets={fetchNextPageAvailableList}
						handleDeleteAssets={handleDeleteAssets}
						hasNextPage={hasNextPageAvailableList}
						isCollapsibleBlockOpen={collapsibleBlocks.availableForRequest}
						isLoadingAssetCartRequest={isLoadingAssetCartRequestAvailable}
						ownersOptions={ownersOptions}
						toggleCollapsibleBlock={toggleCollapsibleBlock}
					/>
					<AssetCartRequestUnavailable
						assetCartRequestUnavailableList={prepareAssetCartRequestList(assetCartRequestListUnavailable)}
						assetTypesOptions={assetTypesOptions}
						getMoreAssets={fetchNextPageUnavailableList}
						handleDeleteAssets={handleDeleteAssets}
						hasNextPage={hasNextPageUnavailableList}
						isCollapsibleBlockOpen={collapsibleBlocks.unavailableForRequest}
						isLoadingAssetCartRequest={isLoadingAssetCartRequestUnavailable}
						ownersOptions={ownersOptions}
						toggleCollapsibleBlock={toggleCollapsibleBlock}
						unavailableAssetRequestCount={unavailableAssetRequestCount}
					/>
				</div>
				<div className={styles.bottomButtonsContainer}>
					<Button
						className={styles.clearAllButton}
						large
						onClick={toggleClearCartConfirm}
						text='Clear all'
						withoutBackground
					/>
					<Button
						disabled={!numberOfFutureRequests}
						large
						onClick={onClickSendRequest}
						text={`Create requests (${numberOfFutureRequests})`}
					/>
				</div>
			</div>
			<Modal
				isOpen={isClearCartConfirm}
				onClose={toggleClearCartConfirm}
				width='520px'
			>
				<Confirm
					isLoading={isLoadingAssetCartRequest}
					negativeBtnText='Cancel'
					onClose={toggleClearCartConfirm}
					onSubmit={onClickClearAll}
					positiveBtnText='Ok'
					question='Are you sure about this?'
				>
					This action will remove all Assets from your Request list.
				</Confirm>
			</Modal>
			<Modal
				isOpen={isCommentsForm}
				onClose={toggleCommentsForm}
				title='Add comments to the request'
				width={MODAL_WIDTH_455}
			>
				<CreateCartRequestsForm
					closeModal={closeCommentsForm}
					handleSendAssetCartRequest={handleSendAssetCartRequest}
					isSingleRequest={numberOfFutureRequests === 1}
				/>
			</Modal>
		</>
	);
};

export default memo(AssetCartRequest);
